import { FILE_FORMAT_SCOPE_TO_PARTY_ROLES, FILE_TYPE, type FileFormatScope } from '@steerfi/shared';
import {
  FileFormatMappingSchema,
  FileFormatMetadataSchema,
  FileFormatSchemaSchema,
  FileFormatValidationRulesSchema,
} from '@steerfi/shared/types/jsonb/file-format.schemas';
import type { ZodSchema } from 'zod';
import { z } from 'zod';
import type {
  CreateFileFormatPayload,
  FileFormatApiRow,
  FileFormatPartySummary,
  UpdateFileFormatPayload,
} from '@/api/file-formats';
import { zodToExample } from '@/lib/zod-to-example';
import type { FileFormatFormFieldErrors, FileFormatFormValues } from './file-format-form.types';

const FILE_TYPE_IDS = Object.values(FILE_TYPE) as [number, ...number[]];

const fileFormatFormSchema = z
  .object({
    code: z
      .string()
      .trim()
      .min(1, 'Code is required')
      .max(50, 'Code must be at most 50 characters'),
    format_name: z
      .string()
      .trim()
      .min(1, 'Name is required')
      .max(100, 'Name must be at most 100 characters'),
    file_type_id: z
      .number()
      .int()
      .refine((v) => FILE_TYPE_IDS.includes(v), { message: 'Select a valid file type' }),
    description: z.string().max(10_000, 'Description must be at most 10,000 characters'),
    s3_bucket_name: z.string().max(255, 'S3 bucket name must be at most 255 characters'),
    version_number: z.number().int().min(1, 'Version must be at least 1'),
    scope: z.enum(['system', 'provider', 'client']),
    party_id: z.string().uuid().nullable(),
  })
  .superRefine((data, ctx) => {
    if (data.scope !== 'system' && !data.party_id) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Select a party for this scope',
        path: ['party_id'],
      });
    }
    if (data.scope === 'system' && data.party_id != null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'System-wide formats must not have a party',
        path: ['party_id'],
      });
    }
  });

export const emptyFormValues = (): FileFormatFormValues => ({
  code: '',
  format_name: '',
  file_type_id: FILE_TYPE.PAYMENT,
  description: '',
  s3_bucket_name: '',
  version_number: 1,
  ...JSONB_DEFAULTS,
  scope: 'system',
  party_id: null,
});

export const inferScopeFromRow = (row: FileFormatApiRow): FileFormatScope => {
  if (row.party_id == null) return 'system';
  const roleId = row.party?.party_role_id;
  if (
    roleId != null &&
    (FILE_FORMAT_SCOPE_TO_PARTY_ROLES.client as readonly number[]).includes(roleId)
  ) {
    return 'client';
  }
  return 'provider';
};

export const rowToFormValues = (row: FileFormatApiRow): FileFormatFormValues => ({
  code: row.code,
  format_name: row.format_name,
  file_type_id: row.file_type_id,
  description: row.description ?? '',
  s3_bucket_name: row.s3_bucket_name ?? '',
  version_number: row.version_number,
  metadata_json: row.metadata_json,
  schema_json: row.schema_json,
  mapping_json: row.mapping_json,
  validation_rules_json: row.validation_rules_json,
  scope: inferScopeFromRow(row),
  party_id: row.party_id,
});

const nullableTrimmed = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
};

const toBasePayload = (values: FileFormatFormValues) => ({
  code: values.code.trim(),
  format_name: values.format_name.trim(),
  file_type_id: values.file_type_id,
  description: nullableTrimmed(values.description),
  s3_bucket_name: nullableTrimmed(values.s3_bucket_name),
  version_number: values.version_number,
  metadata_json: values.metadata_json,
  schema_json: values.schema_json,
  mapping_json: values.mapping_json,
  validation_rules_json: values.validation_rules_json,
});

export const toCreatePayload = (values: FileFormatFormValues): CreateFileFormatPayload => ({
  ...toBasePayload(values),
  party_id: values.scope === 'system' ? null : values.party_id,
});

export const toUpdatePayload = (values: FileFormatFormValues): UpdateFileFormatPayload =>
  toBasePayload(values);

// Single source of truth for the JSONB blob columns surfaced by the form:
// every consumer (validation, reactive error map, advanced-section render
// in FileFormatForm) reads from this one table. Adding a new JSONB column
// is a one-row append here; no other site needs to learn about it.
//
// Each blob is .nullable() at the column level, so a `null` value is the
// canonical "unset" state and is always OK -- the schema only runs against
// non-null values. Validating against the shared @steerfi/shared schemas
// here (rather than only inside the JsonEditor) is what lets the check
// survive a collapsed Advanced section: ref-based validate() would treat
// unmounted editors as valid by default, letting a stored blob that no
// longer matches the current schema escape to the backend as a 400.
type JsonbFieldKey = 'metadata_json' | 'schema_json' | 'mapping_json' | 'validation_rules_json';

export const JSONB_DEFAULTS: Readonly<Pick<FileFormatFormValues, JsonbFieldKey>> = {
  metadata_json: zodToExample(FileFormatMetadataSchema) as FileFormatFormValues['metadata_json'],
  schema_json: zodToExample(FileFormatSchemaSchema) as FileFormatFormValues['schema_json'],
  mapping_json: zodToExample(FileFormatMappingSchema) as FileFormatFormValues['mapping_json'],
  validation_rules_json: zodToExample(
    FileFormatValidationRulesSchema
  ) as FileFormatFormValues['validation_rules_json'],
};

interface JsonbFieldConfig {
  key: JsonbFieldKey;
  schema: ZodSchema;
  /** Field name as rendered to admins (mirrors the DB column name). */
  label: string;
  /** Short helper sentence rendered between Label and Textarea. */
  helper: string;
}

export const JSONB_FIELDS: ReadonlyArray<JsonbFieldConfig> = [
  {
    key: 'metadata_json',
    schema: FileFormatMetadataSchema,
    label: 'metadata_json',
    helper: 'Free-form metadata about this format.',
  },
  {
    key: 'schema_json',
    schema: FileFormatSchemaSchema,
    label: 'schema_json',
    helper: 'Structural definition of the file (columns, fields, record types).',
  },
  {
    key: 'mapping_json',
    schema: FileFormatMappingSchema,
    label: 'mapping_json',
    helper: 'Maps source fields to platform fields.',
  },
  {
    key: 'validation_rules_json',
    schema: FileFormatValidationRulesSchema,
    label: 'validation_rules_json',
    helper: 'Rules applied during parsing or generation.',
  },
];

const formatJsonIssueMessage = (issue: z.ZodIssue): string => {
  const path = issue.path.join('.') || '(root)';
  return `${path}: ${issue.message}`;
};

/**
 * Validate a single JSONB blob against its shared schema. Returns the
 * first issue (formatted as `path: message`) or `null` for unset / valid
 * values. Kept narrow so it can be reused by both the full-form validator
 * and the form's reactive inline error display, holding both to a single
 * source of truth for "what counts as a JSONB error here?".
 */
const validateJsonbField = (value: unknown, schema: ZodSchema): string | null => {
  if (value === null) return null;
  const result = schema.safeParse(value);
  if (result.success) return null;
  const issue = result.error.issues[0];
  return issue ? formatJsonIssueMessage(issue) : 'Invalid value';
};

type JsonbFieldErrors = Partial<Record<JsonbFieldKey, string>>;

/**
 * Compute the JSONB-field error map directly from form values, bypassing
 * the form's submit-time fieldErrors plumbing. Used by `FileFormatForm`
 * to drive the inline error display and the auto-expand of the Advanced
 * section reactively, so a row whose stored blob no longer matches its
 * current shared schema surfaces the problem the moment the modal opens
 * (instead of leaving the user staring at a disabled Save button).
 */
export const collectJsonbErrors = (values: FileFormatFormValues): JsonbFieldErrors => {
  const errors: JsonbFieldErrors = {};
  for (const { key, schema } of JSONB_FIELDS) {
    const message = validateJsonbField(values[key], schema);
    if (message) errors[key] = message;
  }
  return errors;
};

export const validateFileFormatForm = (
  values: FileFormatFormValues
): FileFormatFormFieldErrors | null => {
  const errors: FileFormatFormFieldErrors = {};

  const result = fileFormatFormSchema.safeParse(values);
  if (!result.success) {
    for (const issue of result.error.issues) {
      const key = issue.path[0];
      if (typeof key === 'string' && !errors[key as keyof FileFormatFormFieldErrors]) {
        errors[key as keyof FileFormatFormFieldErrors] = issue.message;
      }
    }
  }

  // Fold in JSONB errors keyed by the canonical config. Iterating the
  // typed table (vs. Object.entries on the result map) preserves
  // `JsonbFieldKey` end-to-end, so the assignment into `errors` is
  // type-checked without a `keyof` cast. First-write wins so a JSONB
  // path-level message in the first pass survives unchanged.
  const jsonbErrors = collectJsonbErrors(values);
  for (const { key } of JSONB_FIELDS) {
    if (errors[key]) continue;
    const message = jsonbErrors[key];
    if (message) errors[key] = message;
  }

  return Object.keys(errors).length > 0 ? errors : null;
};

// Single source of truth for how a party renders inside a scope label:
// applies the 'Unknown' fallback and the missing-code policy. Each public
// formatter composes this with its own framing -- the read-only panel
// adds a "Client/Provider · " prefix and wraps the code in parens; the
// delete dialog uses an inline dot separator because the surrounding
// sentence already brackets the label with parens.
const partyParts = (
  party: FileFormatPartySummary | null | undefined
): { name: string; code: string | null } => ({
  name: party?.display_name ?? 'Unknown',
  code: party?.client_code ?? null,
});

const SYSTEM_WIDE_LABEL = 'System-wide';

export const formatScopeSummary = (
  scope: FileFormatScope,
  party: FileFormatPartySummary | null | undefined
): string => {
  if (scope === 'system') return SYSTEM_WIDE_LABEL;
  const prefix = scope === 'client' ? 'Client' : 'Provider';
  if (party == null) return prefix;
  const { name, code } = partyParts(party);
  return code ? `${prefix} · ${name} (${code})` : `${prefix} · ${name}`;
};

export const formatScopeLabelForDelete = (row: FileFormatApiRow): string => {
  if (row.party_id == null) return SYSTEM_WIDE_LABEL;
  const { name, code } = partyParts(row.party);
  return code ? `${name} · ${code}` : name;
};
